import { useState } from 'react';
import {
  InputLabel,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
  InputAdornment,
} from '@mui/material';
import { jidsNRCForms } from '../../../../../data/forms/nrc/nrcDropdownOptions.js';
import {
  terminalTypeOptions,
  communicationsTypeOptions,
  keyTypeOptions,
} from '../../../../../data/forms/nrc/forwardToSiteDropdownOptions.js';
import { yesNoOptions } from '../../../../../data/forms/generalDropdownOptions.js';
import RetailerLookupButton from '../retailerLookupButton.js';
import axios from 'axios';

const ForwardToSiteForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: '',
    retailerId: '',
    retailerName: '',
    address: '',
    phoneNumber: '',
    terminalType: '',
    retailerComplaint: '',
    thresholdExceeded: '',
    supRequested: '',
    contactName: '',
    callbackNumber: '',
    communicationsType: '',
    keyRequest: '',
    keyType: '',
    serialNumber: '',
    openCase: '',
    caseNumber: '',
    fstAssigned: '',
    gnieArticleNumber: '',
    messageToSite: '',
    messageToSup: '',
    email: user.email,
    emailAddress: user.email,
    displayName: user.displayName,
    userId: user.userId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    if (name === 'supRequested' && value === 'No') {
      setForm(prevForm => ({
        ...prevForm,
        contactName: '',
        callbackNumber: '',
        messageToSup: '',
      }));
    }

    if (name === 'supRequested' && value === 'Yes') {
      setForm(prevForm => ({
        ...prevForm,
        messageToSite: '',
        gnieArticleNumber: '',
      }));
    }

    if (name === 'openCase' && value === 'No') {
      setForm(prevForm => ({
        ...prevForm,
        caseNumber: '',
      }));
    }

    if (name === 'jurisdiction' && value !== 'Indiana') {
      setForm(prevForm => ({
        ...prevForm,
        communicationsType: '',
        retailerComplaint: '',
      }));
    }

    if (name === 'jurisdiction' && value === 'California') {
      setForm(prevForm => ({
        ...prevForm,
        openCase: '',
        caseNumber: '',
        fstAssigned: '',
        gnieArticleNumber: '',
      }));
    }

    if (name === 'jurisdiction' && value !== 'Connecticut') {
      setForm(prevForm => ({
        ...prevForm,
        thresholdExceeded: '',
      }));
    }

    if (name === 'jurisdiction' && value !== 'Texas') {
      setForm(prevForm => ({
        ...prevForm,
        keyRequest: '',
        keyType: '',
        serialNumber: '',
      }));
    }
  };

  const handleClear = () => {
    setForm({
      ...form,
      jurisdiction: '',
      retailerId: '',
      retailerName: '',
      address: '',
      phoneNumber: '',
      terminalType: '',
      retailerComplaint: '',
      thresholdExceeded: '',
      supRequested: '',
      contactName: '',
      callbackNumber: '',
      communicationsType: '',
      keyRequest: '',
      keyType: '',
      serialNumber: '',
      openCase: '',
      caseNumber: '',
      fstAssigned: '',
      gnieArticleNumber: '',
      messageToSite: '',
      messageToSup: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/uscc/fts/submit`,
        data: {
          ...form,
        },
      });
      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      jurisdiction,
      retailerId,
      retailerName,
      address,
      phoneNumber,
      supRequested,
      openCase,
      caseNumber,
      messageToSup,
      callbackNumber,
      contactName,
      communicationsType,
      keyRequest,
      keyType,
      serialNumber,
      retailerComplaint,
      messageToSite,
      gnieArticleNumber,
      fstAssigned,
      thresholdExceeded,
    } = form;

    if (
      !jurisdiction ||
      !retailerId ||
      !retailerName ||
      !address ||
      !phoneNumber ||
      !supRequested
    ) {
      return false;
    }

    if (
      (jurisdiction !== 'California' && !openCase) ||
      (jurisdiction !== 'California' && !fstAssigned)
    ) {
      return false;
    }

    if (jurisdiction === 'Connecticut' && !thresholdExceeded) {
      return false;
    }

    if (openCase === 'Yes' && !caseNumber) {
      return false;
    }

    if (
      (supRequested === 'Yes' && !messageToSup) ||
      (supRequested === 'Yes' && !callbackNumber) ||
      (supRequested === 'Yes' && !contactName)
    ) {
      return false;
    }

    if (
      (jurisdiction === 'Indiana' && !communicationsType) ||
      (jurisdiction === 'Indiana' && !retailerComplaint)
    ) {
      return false;
    }

    if (supRequested === 'No' && !messageToSite) {
      return false;
    }

    if (
      supRequested === 'No' &&
      jurisdiction !== 'California' &&
      !gnieArticleNumber
    ) {
      return false;
    }

    if (jurisdiction === 'Texas' && !keyRequest) {
      return false;
    }

    if (
      jurisdiction === 'Texas' &&
      keyRequest === 'Yes' &&
      (!keyType || !serialNumber)
    ) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>
              {translations.jurisdiction || 'Jurisdiction'}
            </InputLabel>
            <Select
              value={form.jurisdiction}
              label={translations.jurisdiction || 'Jurisdiction'}
              onChange={handleChange}
              name='jurisdiction'
            >
              {jidsNRCForms.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={
              form.retailerId
                ? ''
                : 'Enter a Retailer ID (numeric values only) to lookup'
            }
            arrow
          >
            <TextField
              label='Retailer ID'
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton form={form} setForm={setForm} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label='Retailer Name'
            name='retailerName'
            required
            value={form.retailerName}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Address'
            name='address'
            required
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.phoneNumber}
            onChange={handleChange}
            label='Phone Number'
            name='phoneNumber'
            required
          />
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            required
          >
            <InputLabel>{'Supervisor Requested'}</InputLabel>
            <Select
              value={form.supRequested}
              label={'Supervisor Requested'}
              onChange={handleChange}
              name='supRequested'
            >
              {yesNoOptions.map(option => (
                <MenuItem value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Tooltip
            title={
              form.jurisdiction === 'California'
                ? 'Not Applicable to California Jurisdiction'
                : ''
            }
            arrow
          >
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
              required={form.jurisdiction !== 'California'}
              disabled={form.jurisdiction === 'California'}
            >
              <InputLabel>{'Open Case'}</InputLabel>
              <Select
                value={form.openCase}
                label={'Open Case'}
                onChange={handleChange}
                name='openCase'
              >
                {yesNoOptions.map(option => (
                  <MenuItem value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <Tooltip
            title={
              form.openCase !== 'Yes'
                ? "Please select 'Yes' on Open Case to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              disabled={form.openCase !== 'Yes'}
              required={form.openCase === 'Yes'}
              label='Case Number'
              name='caseNumber'
              fullWidth
              value={form.caseNumber}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Terminal Type'}</InputLabel>
            <Select
              value={form.terminalType}
              label={'Terminal Type'}
              onChange={handleChange}
              name='terminalType'
            >
              {terminalTypeOptions.map(option => (
                <MenuItem value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={
              form.jurisdiction === 'California'
                ? 'Not Applicable to California Jurisdiction'
                : ''
            }
            arrow
          >
            <TextField
              label='FST Assigned to Case'
              name='fstAssigned'
              required={form.jurisdiction !== 'California'}
              disabled={form.jurisdiction === 'California'}
              value={form.fstAssigned}
              onChange={handleChange}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              form.jurisdiction !== 'Connecticut'
                ? 'Please select Connecticut jurisdiction to enable this field'
                : ''
            }
            arrow
          >
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
              required={form.jurisdiction === 'Connecticut'}
              disabled={form.jurisdiction !== 'Connecticut'}
            >
              <InputLabel>{'Threshold Exceeded'}</InputLabel>
              <Select
                value={form.thresholdExceeded}
                label={'Threshold Exceeded'}
                onChange={handleChange}
                name='thresholdExceeded'
              >
                {yesNoOptions.map(option => (
                  <MenuItem value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <Tooltip
            title={
              form.supRequested === 'Yes'
                ? "Please select 'No' on Supervisor Requested to enable this field"
                : form.jurisdiction === 'California'
                  ? 'Not Applicable to California Jurisdiction'
                  : ''
            }
            arrow
          >
            <TextField
              label='GNIE Article Number'
              disabled={
                form.supRequested === 'Yes' ||
                form.jurisdiction === 'California'
              }
              required={
                form.supRequested === 'No' || form.jurisdiction !== 'California'
              }
              name='gnieArticleNumber'
              value={form.gnieArticleNumber}
              onChange={handleChange}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              form.supRequested === 'Yes'
                ? "Please select 'No' on Supervisor Requested to enable this field"
                : null
            }
            arrow
          >
            <TextField
              label={
                <>
                  Brief Summary - <b>(No Cadence Notes)</b>
                </>
              }
              name='messageToSite'
              disabled={form.supRequested === 'Yes'}
              required={form.supRequested === 'No'}
              value={form.messageToSite}
              onChange={handleChange}
              multiline
              rows={4.1}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Tooltip
            title={
              form.jurisdiction !== 'Indiana'
                ? 'Please select Indiana as Jurisdiction to enable.'
                : ''
            }
            arrow
          >
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
              required={form.jurisdiction === 'Indiana'}
              disabled={form.jurisdiction !== 'Indiana'}
            >
              <InputLabel>{'Retailer Complaint'}</InputLabel>
              <Select
                value={form.retailerComplaint}
                label={'Retailer Complaint'}
                onChange={handleChange}
                name='retailerComplaint'
              >
                {yesNoOptions.map(option => (
                  <MenuItem value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <Tooltip
            title={
              form.jurisdiction !== 'Indiana'
                ? 'Please select Indiana as Jurisdiction to enable.'
                : ''
            }
            arrow
          >
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
              required={form.jurisdiction === 'Indiana'}
              disabled={form.jurisdiction !== 'Indiana'}
            >
              <InputLabel>{'Communications Type'}</InputLabel>
              <Select
                value={form.communicationsType}
                label={'Communications Type'}
                onChange={handleChange}
                name='communicationsType'
              >
                {communicationsTypeOptions.map(option => (
                  <MenuItem value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <Tooltip
            title={
              form.jurisdiction !== 'Texas'
                ? 'Please select Texas as Jurisdiction to enable.'
                : ''
            }
            arrow
          >
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
              required={form.jurisdiction === 'Texas'}
              disabled={form.jurisdiction !== 'Texas'}
            >
              <InputLabel>{'Is this a Key Request?'}</InputLabel>
              <Select
                value={form.keyRequest}
                label={'Key Request'}
                onChange={handleChange}
                name='keyRequest'
              >
                {yesNoOptions.map(option => (
                  <MenuItem value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <Tooltip
            title={
              form.keyRequest !== 'Yes'
                ? 'Please select Yes as Key Request to enable.'
                : ''
            }
            arrow
          >
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
              required={form.keyRequest === 'Yes'}
              disabled={form.keyRequest !== 'Yes'}
            >
              <InputLabel>{'Key Type'}</InputLabel>
              <Select
                value={form.keyType}
                label={'Key Type'}
                onChange={handleChange}
                name='keyType'
              >
                {keyTypeOptions.map(option => (
                  <MenuItem value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <Tooltip
            title={
              form.keyRequest !== 'Yes'
                ? 'Please select Yes as Key Request to enable.'
                : ''
            }
            arrow
          >
            <TextField
              label='Serial Number'
              name='serialNumber'
              disabled={form.keyRequest !== 'Yes'}
              required={form.keyRequest === 'Yes'}
              fullWidth
              value={form.serialNumber}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>
          <Tooltip
            title={
              form.supRequested !== 'Yes'
                ? "Please select 'Yes' on Supervisor Requested to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              label='Contact Name'
              name='contactName'
              disabled={form.supRequested !== 'Yes'}
              required={form.supRequested === 'Yes'}
              fullWidth
              value={form.contactName}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>
          <Tooltip
            title={
              form.supRequested !== 'Yes'
                ? "Please select 'Yes' on Supervisor Requested to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              label='Callback Phone Number'
              name='callbackNumber'
              disabled={form.supRequested !== 'Yes'}
              required={form.supRequested === 'Yes'}
              fullWidth
              value={form.callbackNumber}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>

          <Tooltip
            title={
              form.supRequested !== 'Yes'
                ? "Please select 'Yes' on Supervisor Requested to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              label='Message to Supervisor'
              name='messageToSup'
              disabled={form.supRequested !== 'Yes'}
              required={form.supRequested === 'Yes'}
              value={form.messageToSup}
              onChange={handleChange}
              multiline
              rows={4.1}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Tooltip>
        </Grid>
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Tooltip
            title={
              !isFormValid()
                ? 'Please fill in all required fields to enable the Submit button'
                : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};

export default ForwardToSiteForm;
